import { adminRecords } from "./adminRecords";
import { useEffect, useMemo, useState } from "react";
import update from "react-addons-update";
import DataTable from "react-data-table-component";
import { Link, useHistory } from "react-router-dom";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "src/sudokit/components/Dialog";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Text } from "src/sudokit/components/Text";
import VStack from "src/sudokit/Layout/VStack";
import Auth from "../Auth";
import InvocationsSearch from "../components/Admin/InvocationsSearch";
import { useToast } from "../components/ToastWrapper";

function Invocations(props) {
  const [id, setId] = useState("");
  const [chatId, setChatId] = useState("");
  const [chatLookupLoading, setChatLookupLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [invocations, setInvocations] = useState([]);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [billingContexts, setBillingContexts] = useState({});
  // Quick Edit recovery export — small dialog, only available on the per-user list view.
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const showUserColumn = props.showUserColumn !== undefined ? props.showUserColumn : true;
  const { setToast } = useToast();
  const history = useHistory();

  const handleExportQuickEdits = async () => {
    if (!props.userId) return;
    setExportError("");
    setExporting(true);
    try {
      const blob = await Auth.fetchFileWithIdToken(`/api/admin/user/${props.userId}/quick-edits/export`, {
        startDate: exportStartDate ? `${exportStartDate}T00:00:00Z` : undefined,
        endDate: exportEndDate ? `${exportEndDate}T23:59:59Z` : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `quick-edits-${props.userId}-${new Date().toISOString().slice(0, 10)}.txt`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportDialogOpen(false);
    } catch (e) {
      setExportError(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleRedirect = (id) => {
    window.location.href = `/admin/invocation/${id}`;
  };

  const handleChatRedirect = async (rawChatId) => {
    const trimmedChatId = rawChatId.trim();
    if (!trimmedChatId) {
      return;
    }

    setChatLookupLoading(true);
    try {
      const res = await Auth.fetchWithIdToken("/api/admin/agentic-chats/lookup", {
        chatId: trimmedChatId,
      });

      if (!res?.success || !res.chat) {
        setToast("Chat not found");
        return;
      }

      const userIdentifier = res.chat.userEmail || res.chat.userId;
      history.push(`/admin/user/${encodeURIComponent(userIdentifier)}/chats?openChat=${encodeURIComponent(res.chat.id)}`);
    } catch (err) {
      setToast("Unable to look up chat");
    } finally {
      setChatLookupLoading(false);
    }
  };

  async function query(page) {
    setLoading(true);
    const ref = adminRecords.collection("invocations");
    let query;
    let set = {};

    if (queries[page]) {
      query = queries[page];
    } else {
      query = ref.limit(25).orderBy("createdAt", "desc");

      if (page != 1) {
        // look at previous page to build next query
        let result = results[page - 1];

        if (result) {
          query = query.startAfter(result[result.length - 1]);
        }
      }

      setQueries(update(queries, { [page]: { $set: query } }));
    }

    if (props.userId) {
      query = query.where("userId", "==", props.userId);
    }

    let querySnapshot = await query.get();

    if (showUserColumn && props.userCache) {
      await props.userCache.fetchIds(querySnapshot.docs.map((s) => s.get("userId")));
    }

    setInvocations(querySnapshot.docs);
    setResults(update(results, { [page]: { $set: querySnapshot.docs } }));
    setHasSearched(true);
    setLoading(false);
  }

  useEffect(() => {
    // Only auto-search if we have a specific userId (user page context)
    // or if we've already searched once (pagination)
    if (props.userId || hasSearched) {
      query(currentPage);
    }
  }, [currentPage, props.userId, hasSearched]);

  // Auto-search on mount only if we have a userId (user page context)
  useEffect(() => {
    if (props.userId) {
      query(1);
    }
  }, [props.userId]);

  useEffect(() => {
    const fetchBillingContexts = async () => {
      if (!invocations.length) {
        setBillingContexts({});
        return;
      }

      try {
        const invocationIds = invocations.map((doc) => doc.id);
        const res = await Auth.fetchWithIdToken("/api/admin/billing-context/batch", {
          method: "POST",
          invocationIds,
        });
        if (res?.success && res.billingContexts) {
          setBillingContexts(res.billingContexts);
        } else {
          setBillingContexts({});
        }
      } catch (err) {
        setBillingContexts({});
        setToast("Unable to load billing status");
      }
    };

    fetchBillingContexts();
  }, [invocations, setToast]);

  const columns = useMemo(() => {
    const cols = [
      {
        name: "ID",
        width: "240px",
        selector: (row) => {
          return row.id;
        },
        sortable: false,
        cell: (row) => (
          <div className="flex items-center gap-2">
            <Link
              to={"/admin/invocation/" + row.id}
              className="text-app-primary-cta hover:text-app-primary-cta-dark hover:underline transition-colors px-1 py-1 rounded text-md"
              title="View invocation details"
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.id}
            </Link>
          </div>
        ),
      },
      {
        name: "Date",
        width: "160px",
        selector: (row) => {
          return row.get("createdAt").toDate().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
        sortable: false,
        cell: (row) => (
          <div className="text-sm text-gray-600">
            {row.get("createdAt").toDate().toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        ),
      },
      {
        name: "Source",
        selector: (row) => {
          return row.get("source");
        },
        sortable: false,
        cell: (row) => <div className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap">{row.get("source")}</div>,
      },
      {
        name: "Billing",
        width: "140px",
        selector: (row) => billingContexts[row.id]?.status,
        sortable: false,
        cell: (row) => {
          const context = billingContexts[row.id];
          if (!context) {
            return null;
          }

          const status = context.status || "pending";
          if (status === "pending" || status === "no_charge") {
            return null;
          }

          const billingStatusStyles = {
            charged: "bg-green-100 text-green-800 border border-green-200",
            no_charge: "bg-yellow-100 text-yellow-800 border border-yellow-200",
            failed: "bg-red-100 text-red-800 border border-red-200",
            pending: "bg-gray-100 text-gray-600 border border-gray-200",
          };

          const className = `text-xs font-semibold px-2 py-1 rounded capitalize ${billingStatusStyles[status] || billingStatusStyles.pending}`;
          let label = status.replace(/_/g, " ");
          if (status === "failed") {
            label = "Auto-Refund";
          }

          const tooltipParts = [];
          if (context.failure_reason) {
            tooltipParts.push(`Failure: ${context.failure_reason}`);
          }
          if (typeof context.no_output === "boolean") {
            tooltipParts.push(`No output: ${context.no_output ? "yes" : "no"}`);
          }
          const tooltip = tooltipParts.join(" | ") || undefined;

          return (
            <span
              className={className}
              title={tooltip}
            >
              {label}
            </span>
          );
        },
      },
    ];

    if (showUserColumn) {
      cols.splice(2, 0, {
        name: "User",
        width: "200px",
        selector: (row) => {
          const user = props.userCache?.getUser(row.get("userId"));
          return user ? user.displayName || user.email : null;
        },
        sortable: false,
        cell: (row) => {
          const user = props.userCache?.getUser(row.get("userId"));
          if (user) {
            return (
              <Link
                to={"/admin/user/" + user.email}
                className="text-app-primary-cta hover:text-app-primary-cta-dark hover:underline px-1 py-1 rounded text-md transition-colors"
              >
                {user.displayName || user.email}
              </Link>
            );
          }
          return <span className="text-gray-500 italic">No User</span>;
        },
      });
    }

    return cols;
  }, [props.userCache, showUserColumn, billingContexts]);

  return (
    <div className="p-4">
      {!props.userId && (
        <div className="mb-6">
          <VStack gap={2}>
            <Text className="text-base font-medium">Go to Chat</Text>
            <div className="flex items-center gap-3 w-full">
              <input
                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-app-primary-cta focus:border-app-primary-cta placeholder-gray-500"
                type="text"
                placeholder="Chat ID"
                aria-label="Chat ID"
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleChatRedirect(chatId);
                  }
                }}
              />
              <SudoButton
                onClick={() => handleChatRedirect(chatId)}
                variant="primary"
                size="default"
                disabled={!chatId.trim() || chatLookupLoading}
                state={chatLookupLoading ? "loading" : "default"}
              >
                Go
              </SudoButton>
            </div>
          </VStack>
        </div>
      )}

      <div className="mb-6">
        <VStack gap={2}>
          <Text className="text-base font-medium">Go to Invocation</Text>
          <div className="flex items-center gap-3 w-full">
            <input
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-app-primary-cta focus:border-app-primary-cta placeholder-gray-500"
              type="text"
              placeholder="Invocation ID"
              value={id}
              onChange={(e) => setId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleRedirect(id);
                }
              }}
            />
            <SudoButton
              onClick={() => handleRedirect(id)}
              variant="primary"
              size="default"
              disabled={!id.trim()}
            >
              Go
            </SudoButton>
          </div>
        </VStack>
      </div>

      <div className="mb-6">
        <InvocationsSearch
          onRedirect={handleRedirect}
          onSearch={() => {
            setPage(1);
            query(1);
          }}
        />
      </div>

      {(hasSearched || props.userId) && (
        <DataTable
          columns={columns}
          responsive={true}
          progressPending={loading}
          pagination
          paginationServer
          paginationPerPage={25}
          paginationRowsPerPageOptions={[25]}
          paginationTotalRows={99999999}
          onChangePage={(page) => {
            setPage(page);
          }}
          data={invocations}
        />
      )}

      {!hasSearched && !props.userId && !loading && (
        <div className="text-center py-8 text-gray-500">
          <p>Use the search above to find invocations</p>
        </div>
      )}

      {props.userId && (
        <div className="mt-6 pt-4 border-t border-gray-100 text-right">
          <Dialog
            open={exportDialogOpen}
            onOpenChange={(open) => {
              setExportDialogOpen(open);
              if (!open) setExportError("");
            }}
          >
            <DialogTrigger asChild>
              <SudoButton
                type="button"
                variant="tertiary"
                // className="text-xs text-gray-500 hover:text-gray-800 underline"
              >
                Export Quick Edits as plaintext…
              </SudoButton>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Export Quick Edits</DialogTitle>
                <p className="text-sm text-gray-600 mt-2">
                  Bundles this user's Quick Edit invocations into a plaintext file. Each section includes the original passage, the user's instruction, and the AI's streamed output. Useful for
                  reconstructing lost content. Capped at 100 invocations per export.
                </p>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-3 max-w-[90%]">
                  <label className="block text-sm font-medium text-gray-700 max-w-[90%]">
                    Start date <span className="font-normal text-gray-400">(UTC)</span>
                    <input
                      type="date"
                      value={exportStartDate}
                      onChange={(e) => setExportStartDate(e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-cta"
                      disabled={exporting}
                    />
                  </label>
                  <label className="block text-sm font-medium text-gray-70 max-w-[90%]">
                    End date <span className="font-normal text-gray-400">(UTC)</span>
                    <input
                      type="date"
                      value={exportEndDate}
                      onChange={(e) => setExportEndDate(e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-cta"
                      disabled={exporting}
                    />
                  </label>
                </div>
                {exportError && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{exportError}</div>}
              </div>
              <DialogFooter>
                <SudoButton
                  variant="tertiary"
                  onClick={() => setExportDialogOpen(false)}
                  disabled={exporting}
                >
                  Cancel
                </SudoButton>
                <SudoButton
                  variant="primary"
                  onClick={handleExportQuickEdits}
                  disabled={exporting}
                >
                  {exporting ? "Exporting…" : "Download .txt"}
                </SudoButton>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}

export default Invocations;
